import { useCallback, useEffect, useRef, useState } from 'react'

import {
	extractImageFilesFromClipboard,
	extractImageFilesFromDragEvent,
} from '../lib/imageFileExtraction'

export type UseImageDropAndPasteOptions = {
	onImages: (files: File[]) => void
	onActiveChange?: (active: boolean) => void
	enablePaste?: boolean
	enableDrop?: boolean
}

/**
 * 在指定容器上监听粘贴(Ctrl+V)与拖拽事件，提取其中的图片 File 并回调。
 *
 * 关键实现点：
 * <ul>
 *   <li>dragover 必须无条件 preventDefault()，否则浏览器不触发 drop；
 *       图片识别只在 dragenter / drop 时做（dragover 时 dataTransfer 通常为空）。</li>
 *   <li>所有拖拽 handler 闭包在 callback ref 内部，避免 stale closure。</li>
 *   <li>用 dragDepth 计数防止嵌套元素引起的"假离开"闪烁。</li>
 * </ul>
 */
export function useImageDropAndPaste({
	onImages,
	onActiveChange,
	enablePaste = true,
	enableDrop = true,
}: UseImageDropAndPasteOptions) {
	const [dragActive, setDragActive] = useState(false)
	const dragDepthRef = useRef(0)
	const cleanupRef = useRef<(() => void) | null>(null)

	const fireImages = useCallback(
		(files: File[]) => {
			if (files.length > 0) onImages(files)
		},
		[onImages],
	)

	const setActive = useCallback(
		(active: boolean) => {
			setDragActive((prev) => {
				if (prev !== active) onActiveChange?.(active)
				return active
			})
		},
		[onActiveChange],
	)

	const containerRef = useCallback(
		(node: HTMLElement | null) => {
			// 先清理旧 listener
			cleanupRef.current?.()
			cleanupRef.current = null
			dragDepthRef.current = 0
			setActive(false)

			if (!node || !enableDrop) return

			const isInside = (t: EventTarget | null): boolean =>
				Boolean(t instanceof Node && node.contains(t))

			const onDragEnter = (e: DragEvent) => {
				if (!isInside(e.target)) return
				// 关键：dragenter 也无条件激活高亮（不检查图片类型）。
				// Chromium 在 dragenter 时 dataTransfer 的 items 虽然存在但 getAsFile()
				// 可能仍在异步准备中，此时检查会过早返回导致用户看不到任何反馈。
				e.preventDefault()
				dragDepthRef.current += 1
				setActive(true)
			}
			const onDragOver = (e: DragEvent) => {
				if (!isInside(e.target)) return
				// 关键：无条件 preventDefault + stopPropagation，让浏览器允许 drop
				e.preventDefault()
				e.stopPropagation()
			}
			const onDragLeave = (e: DragEvent) => {
				if (!isInside(e.target)) return
				e.preventDefault()
				dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
				if (dragDepthRef.current === 0) setActive(false)
			}
			const onDrop = (e: DragEvent) => {
				if (!isInside(e.target)) return
				e.preventDefault()
				e.stopPropagation()
				dragDepthRef.current = 0
				setActive(false)
				fireImages(extractImageFilesFromDragEvent(e))
			}

			node.addEventListener('dragenter', onDragEnter as EventListener)
			node.addEventListener('dragover', onDragOver as EventListener)
			node.addEventListener('dragleave', onDragLeave as EventListener)
			node.addEventListener('drop', onDrop as EventListener)

			cleanupRef.current = () => {
				node.removeEventListener('dragenter', onDragEnter as EventListener)
				node.removeEventListener('dragover', onDragOver as EventListener)
				node.removeEventListener('dragleave', onDragLeave as EventListener)
				node.removeEventListener('drop', onDrop as EventListener)
			}
		},
		[enableDrop, fireImages, setActive],
	)

	useEffect(() => {
		return () => {
			cleanupRef.current?.()
			cleanupRef.current = null
		}
	}, [])

	useEffect(() => {
		if (!enablePaste) return
		const handler = (e: ClipboardEvent) => {
			const files = extractImageFilesFromClipboard(e)
			if (files.length > 0) {
				e.preventDefault()
				fireImages(files)
			}
		}
		document.addEventListener('paste', handler as EventListener)
		return () => document.removeEventListener('paste', handler as EventListener)
	}, [enablePaste, fireImages])

	return { containerRef, dragActive }
}
